import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Row, Col, FormGroup, Input, Label } from 'reactstrap';

import MultiSelectDropdown from 'components/layout/forms/MultiSelectDropdown';
import IProductId from 'interfaces/products/IProductId';
import IContact from 'interfaces/contacts/IContact';
import SearchableSelectField from 'components/layout/forms/SearchableSelectField';
import TextInputField from 'components/layout/forms/TextInputField';
import {
  MATERIAL_CATEGORY,
  PRODUCT_DELIVERY_CATEGORY,
  toOptions,
  useChoiceLabelOf,
  useChoiceList,
} from 'services/offers/offerChoices';
import translations from 'services/translations/translations';
import { fetchCategories, fetchTagTypes } from 'store/products/actions';

import getSupplierOptions from './src/product/getSupplierOptions';
import { showTagOptions } from './ProductSelectOptions';
import showIDFields from './showIDFields';

const ProductBasicInfo = ({
  idValues,
  addIdValue,
  formState,
  handleFormState,
  updateSupplier,
  suppliers,
  categories,
  handleCategories,
  supplierId,
  deliveryCategory,
  updateDeliveryCategory,
  handleTags,
  tagTypesList,
  tags,
  fetchCategoryTypesList,
  fetchTagTypesList,
}: {
  idValues: IProductId[];
  addIdValue: any;
  formState: any;
  handleFormState: any;
  updateSupplier: any;
  suppliers: IContact[];
  categories: any;
  handleCategories: any;
  supplierId: null | number;
  deliveryCategory: string;
  updateDeliveryCategory: any;
  tags: any;
  handleTags: any;
  tagTypesList: string[];
  fetchCategoryTypesList: any;
  fetchTagTypesList: () => void;
}): JSX.Element | null => {
  const { t } = useTranslation([
    translations.offers,
    translations.common,
    translations.products,
  ]);

  useEffect(() => {
    fetchCategoryTypesList();
    fetchTagTypesList();
  }, [fetchCategoryTypesList, fetchTagTypesList]);

  // The categories and the delivery categories of the choice lists the admins manage, with
  // their names (also those the admins added)
  const categoryValues: string[] = (categories ?? []).map(
    (category: { value: string }) => category.value
  );
  const categoryOptions = useChoiceList(MATERIAL_CATEGORY, null);
  const categoryLabelOf = useChoiceLabelOf(MATERIAL_CATEGORY);
  const deliveryCategoryOptions = useChoiceList(
    PRODUCT_DELIVERY_CATEGORY,
    deliveryCategory
  );

  return (
    <>
      <Row>{showIDFields(idValues, addIdValue, t, translations)}</Row>
      <Row>
        <Col md={4}>
          <TextInputField
            value={formState.name}
            label={`${t(`${translations.products}:Name`)} *`}
            required
            id="name"
            name="name"
            onChange={handleFormState}
          />
        </Col>
        <Col md={4}>
          <TextInputField
            value={formState.displayName}
            label={`${t(`${translations.products}:Display Name`)} *`}
            required
            id="displayName"
            name="displayName"
            onChange={handleFormState}
          />
        </Col>
        <Col md={4}>
          <TextInputField
            value={formState.sPName}
            label={`${t(`${translations.products}:Supplier Product Name`)} *`}
            required
            id="sPName"
            name="sPName"
            onChange={handleFormState}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup>
            <Label className="label">
              {t(`${translations.products}:Category`)}
            </Label>
            <MultiSelectDropdown
              placeholder={t(`${translations.products}:Select`)}
              required
              name="category"
              value={(categories ?? []).map(
                (category: { label: string; value: string }) => ({
                  label: categoryLabelOf(category.value, category.label),
                  value: category.value,
                })
              )}
              onChange={handleCategories}
              options={categoryOptions.filter(
                (option) => !categoryValues.includes(option.value)
              )}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <SearchableSelectField
            label={`${t(`${translations.products}:Supplier`)} *`}
            required
            id="supplierID"
            name="supplierID"
            value={supplierId === null ? '' : supplierId}
            onChange={updateSupplier}
            options={getSupplierOptions(suppliers, supplierId)}
          />
        </Col>
        <Col md={4}>
          <SearchableSelectField
            label={`${t(`${translations.products}:Delivery category`)} *`}
            required
            id="deliveryCategory"
            name="deliveryCategory"
            value={deliveryCategory}
            onChange={updateDeliveryCategory}
            options={toOptions(deliveryCategoryOptions)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup>
            <Label className="label">
              {t(`${translations.products}:Tags`)}
            </Label>
            <MultiSelectDropdown
              placeholder={t(`${translations.products}:Select`)}
              required
              name="tags"
              value={tags}
              onChange={handleTags}
              options={
                tagTypesList
                  ? showTagOptions(tagTypesList, translations, t)
                  : ''
              }
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          {/* A free description of the product in words */}
          <FormGroup>
            <Label className="label" for="productMemo">
              {t(`${translations.products}:Memo`)}
            </Label>
            <Input
              className="text-field product-memo"
              id="productMemo"
              name="memo"
              onChange={handleFormState}
              placeholder={t(`${translations.products}:Memo placeholder`)}
              rows={4}
              type="textarea"
              value={formState.memo ?? ''}
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  categoryTypesList: state.products.categoryTypes,
  tagTypesList: state.products.tagTypes,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchCategoryTypesList: () => dispatch(fetchCategories()),
  fetchTagTypesList: () => dispatch(fetchTagTypes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductBasicInfo);
